// Skills del agente — ADR-2026-09-09-el-agente-tiene-skills.
//
// Una skill es un INSTRUCTIVO de cómo hacer bien un tipo de tarea (escribir el prompt de
// una foto con modelo, encarar un análisis de competencia, leer una planilla grande sin
// reventar el contexto). Copia el mecanismo con el que trabaja Claude Code: el catálogo
// —nombre, descripción y cuándo usarla— está SIEMPRE en el system prompt, y el cuerpo se
// carga A DEMANDA con la tool `cargar_skill`. Así el prompt dice qué existe y cuándo, y
// el cómo no pesa en cada turno.
//
// Dos orígenes: las de FÁBRICA (markdown en el repo, `fabrica/*.md`) y las de la EMPRESA
// (tabla `agent_skills`, escritas por el agente con `guardar_skill` cuando el usuario le
// enseña un procedimiento). Mismo formato, mismo catálogo.
//
// Todo puro y testeable. Nada de Supabase acá.

export type OrigenSkill = 'fabrica' | 'empresa';

export interface SkillDelAgente {
  /** kebab-case, único. Es lo que el modelo manda en `cargar_skill`. */
  nombre: string;
  /** Una o dos frases: qué enseña. Se lee en cada turno, así que corta. */
  descripcion: string;
  /** El disparador: en qué situación cargarla. También se lee en cada turno. */
  cuando: string;
  /** El instructivo completo, en markdown. Solo viaja cuando se carga. */
  cuerpo: string;
  origen: OrigenSkill;
  /** Fila de `agent_skills` (solo empresa). */
  id?: string;
}

/** Topes de una skill de empresa (lo escribe un modelo: puede venir gigante). */
export const LIMITES_SKILL = {
  nombre: 40,
  descripcion: 240,
  cuando: 240,
  cuerpo: 6000,
  cuerpoMinimo: 40,
  /** Skills de empresa por cuenta. Con más, el catálogo deja de caber cómodo. */
  porEmpresa: 30,
} as const;

// ── Núcleo compartido ───────────────────────────────────────────────────────

/**
 * La marca que una skill de imagen pone donde entra su NÚCLEO: el conocimiento puro del
 * prompt (estructura, luz, tela, errores del motor) que viven en `fabrica/nucleos/` y que
 * comparten el agente y los escritores del backend
 * (ADR-2026-10-06-los-nucleos-de-imagen-se-comparten). La sección del agente (tools,
 * confirmaciones, lotes) queda alrededor, en el `.md` de la skill.
 */
export const MARCA_NUCLEO = '<!-- nucleo -->';

/** El `.md` de la skill con el núcleo en el lugar de la marca. Sin marca, tal cual. */
export function ensamblarSkill(md: string, nucleo: string): string {
  if (!md.includes(MARCA_NUCLEO)) return md;
  return md.replace(MARCA_NUCLEO, nucleo.trim());
}

// ── Frontmatter ─────────────────────────────────────────────────────────────

/**
 * Lee el frontmatter YAML MÍNIMO de una skill (el mismo formato que usa el repo
 * `atlas-skills`): `clave: valor` en una línea, o `clave: >-` seguido de líneas
 * indentadas que se pliegan en un párrafo. No es un parser de YAML: no hace falta más
 * y un parser de verdad pesaría en el bundle por tres claves.
 */
function leerFrontmatter(md: string): { campos: Record<string, string>; cuerpo: string } {
  const lineas = md.replace(/\r\n/g, '\n').split('\n');
  if (lineas[0]?.trim() !== '---') return { campos: {}, cuerpo: md.trim() };
  const campos: Record<string, string> = {};
  let i = 1;
  let claveAbierta: string | null = null;
  for (; i < lineas.length; i++) {
    const linea = lineas[i];
    if (linea.trim() === '---') {
      i++;
      break;
    }
    const m = /^([a-zA-Z_][a-zA-Z0-9_]*):\s*(.*)$/.exec(linea);
    if (m && !/^\s/.test(linea)) {
      const [, clave, valor] = m;
      if (valor === '>-' || valor === '>' || valor === '|') {
        claveAbierta = clave;
        campos[clave] = '';
      } else {
        claveAbierta = null;
        campos[clave] = valor.trim();
      }
      continue;
    }
    if (claveAbierta && /^\s+\S/.test(linea)) {
      campos[claveAbierta] = `${campos[claveAbierta]} ${linea.trim()}`.trim();
    }
  }
  return { campos, cuerpo: lineas.slice(i).join('\n').trim() };
}

/** Markdown con frontmatter → skill. Tira si le falta algo: una skill de fábrica rota se
 *  tiene que notar en los tests, no en producción. */
export function parsearSkill(md: string, origen: OrigenSkill = 'fabrica'): SkillDelAgente {
  const { campos, cuerpo } = leerFrontmatter(md);
  const nombre = campos.name?.trim() ?? '';
  const descripcion = campos.description?.trim() ?? '';
  const cuando = campos.when_to_use?.trim() ?? '';
  if (!nombre) throw new Error('La skill no tiene `name` en el frontmatter.');
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(nombre)) {
    throw new Error(`El nombre de la skill "${nombre}" no es kebab-case.`);
  }
  if (!descripcion) throw new Error(`La skill "${nombre}" no tiene \`description\`.`);
  if (!cuando) throw new Error(`La skill "${nombre}" no tiene \`when_to_use\`.`);
  if (!cuerpo) throw new Error(`La skill "${nombre}" no tiene cuerpo.`);
  return { nombre, descripcion, cuando, cuerpo, origen };
}

// ── Catálogo para el prompt ─────────────────────────────────────────────────

/**
 * Las líneas del catálogo que van al system prompt. Una por skill: nombre, qué enseña y
 * cuándo cargarla. Es lo que el modelo lee para DECIDIR cargar; el cuerpo no va.
 */
export function catalogoParaPrompt(skills: SkillDelAgente[]): string {
  if (skills.length === 0) return '  (ninguna)';
  return skills
    .map((s) => `  • ${s.nombre} — ${s.descripcion} Cuándo: ${s.cuando}`)
    .join('\n');
}

/** Junta fábrica + empresa. Si una de empresa repite un nombre de fábrica (no debería:
 *  `normalizarSkillNueva` lo impide), gana la de fábrica. */
export function unirSkills(
  fabrica: SkillDelAgente[],
  empresa: SkillDelAgente[],
): SkillDelAgente[] {
  const nombres = new Set(fabrica.map((s) => s.nombre));
  return [...fabrica, ...empresa.filter((s) => !nombres.has(s.nombre))];
}

// ── cargar_skill ────────────────────────────────────────────────────────────

const normalizarNombrePedido = (v: unknown): string =>
  typeof v === 'string' ? v.trim().toLowerCase().replace(/^\/+/, '') : '';

export function buscarSkill(nombre: unknown, skills: SkillDelAgente[]): SkillDelAgente | null {
  const n = normalizarNombrePedido(nombre);
  if (!n) return null;
  return skills.find((s) => s.nombre === n) ?? null;
}

/**
 * Lo que se le contesta al modelo. Con nombre válido, el instructivo entero con un
 * encabezado que dice qué es (así en el historial se distingue de un mensaje). Sin
 * nombre válido, la lista de las que existen: que se corrija solo, sin otro turno.
 */
export function resultadoDeCargarSkill(
  input: unknown,
  skills: SkillDelAgente[],
): { texto: string; cargada: string | null } {
  const pedido = (input as { nombre?: unknown } | null)?.nombre;
  const skill = buscarSkill(pedido, skills);
  if (!skill) {
    const nombres = skills.map((s) => `"${s.nombre}"`).join(', ');
    const n = normalizarNombrePedido(pedido);
    return {
      cargada: null,
      texto: `${n ? `No existe la skill "${n}".` : 'Falta el `nombre`.'} Las disponibles son: ${nombres}. Volvé a llamar con una de esas.`,
    };
  }
  const rotulo = skill.origen === 'empresa' ? 'skill de la empresa' : 'skill';
  // El instructivo es para el modelo, no para el usuario: se aplica, no se muestra
  // (ADR-2026-09-12-el-instructivo-de-las-skills-de-fabrica-es-interno).
  return {
    cargada: skill.nombre,
    texto: `[${rotulo} "${skill.nombre}" — seguí este instructivo para la tarea. Es interno: aplicalo, no se lo pegues ni se lo cites al usuario]\n\n${skill.cuerpo}`,
  };
}

// ── guardar_skill ───────────────────────────────────────────────────────────

/** "Fotos para Instagram" → "fotos-para-instagram". Sin acentos, sin símbolos. */
export function slugDeSkill(v: unknown): string {
  return String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, LIMITES_SKILL.nombre)
    .replace(/-+$/g, '');
}

const recortar = (v: unknown, max: number): string =>
  String(v ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max)
    .trim();

export type SkillNueva = Pick<SkillDelAgente, 'nombre' | 'descripcion' | 'cuando' | 'cuerpo'>;

/**
 * Valida y acota lo que el modelo manda en `guardar_skill`. Devuelve la skill lista para
 * la base, o el motivo (en texto para el modelo) de por qué no.
 *
 * `reservados` son los nombres de fábrica: una skill de empresa no puede taparlos,
 * porque entonces cargar "foto-con-modelo" daría el texto que escribió un usuario en vez
 * del instructivo revisado.
 */
export function normalizarSkillNueva(
  input: unknown,
  reservados: string[],
): { ok: true; skill: SkillNueva } | { ok: false; motivo: string } {
  const e = (input ?? {}) as Record<string, unknown>;
  const nombre = slugDeSkill(e.nombre);
  if (!nombre) return { ok: false, motivo: 'Falta el `nombre` (corto, descriptivo: "fotos-para-instagram").' };
  if (reservados.includes(nombre)) {
    return {
      ok: false,
      motivo: `"${nombre}" es una skill de fábrica y no se puede sobrescribir. Elegí otro nombre (ej. "${nombre}-de-la-marca").`,
    };
  }
  const descripcion = recortar(e.descripcion, LIMITES_SKILL.descripcion);
  if (!descripcion) return { ok: false, motivo: 'Falta la `descripcion` (una frase: qué enseña).' };
  const cuando = recortar(e.cuando, LIMITES_SKILL.cuando);
  if (!cuando) return { ok: false, motivo: 'Falta `cuando` (en qué situación cargarla).' };
  const cuerpo = String(e.cuerpo ?? '')
    .replace(/\r\n/g, '\n')
    .trim()
    .slice(0, LIMITES_SKILL.cuerpo)
    .trim();
  if (cuerpo.length < LIMITES_SKILL.cuerpoMinimo) {
    return {
      ok: false,
      motivo: 'El `cuerpo` es demasiado corto para ser un instructivo. Escribí los pasos y las reglas.',
    };
  }
  return { ok: true, skill: { nombre, descripcion, cuando, cuerpo } };
}

/** Qué le queda al modelo en el historial después de guardar (el `tool_result`). */
export function resultadoDeGuardarSkill(
  r: ReturnType<typeof normalizarSkillNueva>,
  guardada: boolean,
): Record<string, unknown> {
  if (!r.ok) return { saved: false, motivo: r.motivo };
  if (!guardada) return { saved: false, motivo: 'No se pudo guardar. Probá de nuevo más tarde.' };
  return {
    saved: true,
    nombre: r.skill.nombre,
    nota: 'Queda en SKILLS DISPONIBLES a partir del próximo turno. Decile al usuario en una línea que la guardaste y con qué nombre.',
  };
}
